import { ProductType, UnitOfMeasure } from '@prisma/client'
import { z } from 'zod'
import { mediaInputSchema } from './media-schema'

export const productSchema = z.object({
  // Required core fields
  name: z.string().min(1, 'Name is required'),

  // Required fields with defaults
  description: z.string().default(''),
  slug: z.string().default(''),
  published: z.boolean().default(false),
  isDeleted: z.boolean().default(false),
  isFeatured: z.boolean().default(false),
  isSponsored: z.boolean().default(false),
  isPromoted: z.boolean().default(false),
  price: z.number().min(0).default(0),
  categoryId: z.string().default('uncategorized'),
  unitOfMeasure: z.nativeEnum(UnitOfMeasure).default(UnitOfMeasure.EACH),
  type: z.nativeEnum(ProductType).default(ProductType.PHYSICAL),

  // Optional fields
  media: z.array(mediaInputSchema).default([]),
  salePrice: z.number().nullable().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  keywords: z.array(z.string()).default([]),
  attributes: z.record(z.string(), z.unknown()).optional().default({}),

  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
})

export type ProductData = z.infer<typeof productSchema>
export type ProductUpdateData = Partial<ProductData>

// Unit of measure settings schema - these are stored in variants, not products
export const productUnitSettingsSchema = z.object({
  minOrderQuantity: z.number().min(0).default(0),
  maxOrderQuantity: z.number().min(0).nullable().optional(),
  incrementStep: z.number().min(0).default(0),
  rollLengthUnits: z.number().min(0).nullable().optional(),
  rollPrice: z.number().min(0).nullable().optional()
})

export type ProductUnitSettings = z.infer<typeof productUnitSettingsSchema>

// TODO: This is a simplified variant schema used for specific UI components
// Consider migrating to use the full productVariantSchema from product-variant-schema.ts
export const productVariantSchema = z.object({
  // Required core fields
  productId: z.string().min(1, 'Product ID is required'),
  sku: z.string().min(1, 'SKU is required'),
  name: z.string().min(1, 'Name is required'),
  color: z.string().min(1, 'Color is required'),
  widthInches: z.number().min(0, 'Width must be at least 0'),
  heightInches: z.number().min(0, 'Height must be at least 0'),
  pricePerUnit: z.number().min(0, 'Price per yard must be at least 0'),
  unitOfMeasure: z.string().default(''),
  minOrderQuantity: z.number().min(0).default(0),
  maxOrderQuantity: z.number().min(0).default(0),
  incrementStep: z.number().min(0).default(0),
  rollLengthUnits: z.number().min(0).default(0),

  // Optional fields
  rollLengthYards: z.number().min(0).nullable().optional(),
  rollPrice: z.number().min(0).nullable().optional(),
  minimumOrderYards: z.number().min(0).default(1),
  attributes: z.record(z.string(), z.any()).optional().nullable(),
  media: z.array(mediaInputSchema).default([]),
  mediaId: z.string().optional().nullable(),

  // System fields
  id: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional()
})

export type ProductVariantData = z.infer<typeof productVariantSchema>
export type ProductVariantUpdateData = Partial<ProductVariantData>

export const productInventoryUpdateSchema = z.object({
  inventories: z.array(
    z
      .object({
        sku: z.string().min(1, 'SKU is required'),
        quantity: z.number().min(0, 'Quantity must be at least 0'),
        remainingYards: z.number().min(0, 'Remaining yards must be at least 0').optional().nullable(),
        reorderPoint: z.number().min(0, 'Reorder point must be at least 0').default(5),
        reorderQuantity: z.number().min(1, 'Reorder quantity must be at least 1').default(10),
        shopLocationId: z.string().min(1, 'Shop location is required'),
        variantId: z.string().optional().nullable(),
        productId: z.string().optional().nullable() // Optional since we'll get it from the variant
      })
      .refine(
        (data) => (data.productId && !data.variantId) || (!data.productId && data.variantId),
        'Either productId or variantId must be provided, but not both'
      )
  )
})

export type ProductInventoryData = z.infer<typeof productInventoryUpdateSchema>
